import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ApiAcuerdosGestores, PoseedorControllerService } from 'api-rest';
import { forkJoin, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SnackbarService } from '../../services/snackbar.service';
import { SpinnerService } from '../../services/spinner.service';

@Component({
  selector: 'managers-agreements-consent-holders-modal',
  templateUrl: './managers-agreements-consent-holders-modal.component.html',
  styleUrls: ['./managers-agreements-consent-holders-modal.component.scss']
})
export class ManagersAgreementsConsentHoldersModalComponent implements OnInit {
  formGroup: FormGroup;
  consentimiento: any;
  poseedoresDisponibles: any[] = [];
  poseedoresConsentimiento: any[] = [];
  selectedPoseedorIds: number[] = [];
  poseedoresMismoDocumentoNoIncluidos: any[] = [];
  selectedAvailablePoseedorIds: number[] = [];
  includedHeadArray: any[] = [
    { Head: 'Razón social', FieldName: 'razonSocial', Tooltip: true },
    { Head: 'NIMA', FieldName: 'nima' },
    { Head: 'Denominación de centro', renderValue: (item: any) => item?.denominacionCentro ?? item?.centro ?? '-' },
    { Head: 'Seleccionar', FieldName: 'actions', check: true, buttons: [], width: '1', permanent: true },
  ];
  availableHeadArray: any[] = [
    { Head: 'Razón social', FieldName: 'razonSocial', Tooltip: true },
    { Head: 'NIMA', FieldName: 'nima' },
    { Head: 'Denominación de centro', renderValue: (item: any) => item?.denominacionCentro ?? item?.centro ?? '-' },
    { Head: 'Seleccionar', FieldName: 'actions', check: true, buttons: [], width: '1', permanent: true },
  ];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ManagersAgreementsConsentHoldersModalComponent>,
    private formBuilder: FormBuilder,
    public spinnerSrv: SpinnerService,
    private snackBarSrv: SnackbarService,
    private poseedorControllerService: PoseedorControllerService,
    @Inject(ApiAcuerdosGestores.ConsentimientoControllerService)
    private consentimientoControllerSrv: ApiAcuerdosGestores.ConsentimientoControllerService,
    @Inject(ApiAcuerdosGestores.ConsentimientoPoseedorControllerService)
    private consentimientoPoseedorControllerSrv: ApiAcuerdosGestores.ConsentimientoPoseedorControllerService,
    @Inject(ApiAcuerdosGestores.ConsentimientoEstadosControllerService)
    private consentimientoEstadosControllerSrv: ApiAcuerdosGestores.ConsentimientoEstadosControllerService,
  ) {
    this.formGroup = this.formBuilder.group({
      poseedoresNuevos: [[]],
    });
  }

  ngOnInit(): void {
    this.consentimiento = this.data?.consentimiento ?? {};
    this.poseedoresDisponibles = this.data?.poseedoresDisponibles ?? [];
    this.poseedoresConsentimiento = this.withCheckedFlag((this.consentimiento?.poseedores ?? []).filter((item: any) => !!item?.id), this.selectedPoseedorIds);
    this.loadPoseedoresMismoDocumentoNoIncluidos();
  }

  cerrar(): void {
    this.dialogRef.close();
  }

  getPoseedorLabel(poseedor: any): string {
    const razonSocial = poseedor?.razonSocial ?? '';
    const nima = poseedor?.nima ?? '';
    const centro = poseedor?.denominacionCentro ?? poseedor?.centro ?? '';
    return [razonSocial, nima, centro].filter(Boolean).join(' - ');
  }

  canDelete(): boolean {
    const estado = `${this.consentimiento?.estado?.descripcionCorta ?? ''} ${this.consentimiento?.estado?.descripcion ?? ''}`.toLowerCase();
    const estadoCompletadoOValidado = estado.includes('completado') || estado.includes('validado') || estado.includes('baja');
    return !estadoCompletadoOValidado;
  }

  addNimas(): void {
    const nuevosPoseedores: any[] = this.poseedoresMismoDocumentoNoIncluidos.filter((item: any) =>
      this.selectedAvailablePoseedorIds.includes(item?.id)
    );
    if (!nuevosPoseedores.length) {
      this.snackBarSrv.showSnackBar('Debe seleccionar al menos un NIMA para añadir', 'error');
      return;
    }

    const mergedById = new Map<number, any>();
    this.poseedoresConsentimiento.forEach((item: any) => {
      if (item?.id) {
        mergedById.set(item.id, item);
      }
    });
    nuevosPoseedores.forEach((item: any) => {
      if (item?.id) {
        mergedById.set(item.id, item);
      }
    });

    const poseedoresActualizados = Array.from(mergedById.values());
    this.updateConsentPoseedores(poseedoresActualizados, 'NIMAs añadidos correctamente');
  }

  removeOrDeactivateSelected(): void {
    if (!this.selectedPoseedorIds.length) {
      this.snackBarSrv.showSnackBar('Debe seleccionar al menos un NIMA', 'error');
      return;
    }

    if (this.canDelete()) {
      const poseedoresRestantes = this.poseedoresConsentimiento.filter((item: any) => !this.selectedPoseedorIds.includes(item?.id));
      this.updateConsentPoseedores(poseedoresRestantes, 'NIMAs borrados correctamente');
      return;
    }

    this.deactivateSelectedPoseedores();
  }

  private updateConsentPoseedores(poseedores: any[], successMessage: string): void {
    if (!this.consentimiento?.id) {
      this.snackBarSrv.showSnackBar('No se pudo identificar el consentimiento', 'error');
      return;
    }

    const payload: ApiAcuerdosGestores.ConsentimientoDTO = {
      ...this.consentimiento,
      poseedores,
    } as any;

    this.spinnerSrv.loadingShow();
    this.consentimientoControllerSrv.updateById1(this.consentimiento.id, payload).subscribe({
      next: () => {
        this.spinnerSrv.loadingHide();
        this.poseedoresConsentimiento = this.withCheckedFlag(poseedores, []);
        this.selectedPoseedorIds = [];
        this.selectedAvailablePoseedorIds = [];
        this.loadPoseedoresMismoDocumentoNoIncluidos();
        this.consentimiento = { ...this.consentimiento, poseedores };
        this.snackBarSrv.showSnackBar(successMessage, 'success');
      },
      error: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Ha ocurrido un error al actualizar los NIMAs del consentimiento', 'error');
      },
    });
  }

  private deactivateSelectedPoseedores(): void {
    if (!this.consentimiento?.id || !this.data?.acuerdoIndirecto?.id) {
      this.snackBarSrv.showSnackBar('No se pudo identificar el consentimiento', 'error');
      return;
    }

    const filtros = this.selectedPoseedorIds.map((poseedorId) => ({
      acuerdoIndirecto: { id: this.data.acuerdoIndirecto.id },
      poseedores: { id: poseedorId },
      consentimiento: { id: this.consentimiento.id },
    } as any));

    this.spinnerSrv.loadingShow();
    forkJoin(
      filtros.map((filtro: any) =>
        this.consentimientoPoseedorControllerSrv.findAll1(filtro, { page: 0, size: 25, sort: ['id,desc'] })
      )
    ).pipe(
      switchMap((responses: any[]) => {
        const consentimientosPoseedor = responses
          .flatMap((response: any) => response?.datos ?? [])
          .filter((row: any) => row?.id && row?.consentimiento?.id === this.consentimiento.id);

        if (!consentimientosPoseedor.length) {
          this.spinnerSrv.loadingHide();
          this.snackBarSrv.showSnackBar('No se encontraron registros para dar de baja los NIMAs seleccionados', 'error');
          return of(null);
        }

        return this.consentimientoEstadosControllerSrv.findAll6().pipe(
          switchMap((estados: any[]) => {
            const estadoBaja = (estados ?? []).find((estado: any) =>
              `${estado?.descripcionCorta ?? ''} ${estado?.descripcion ?? ''}`.toLowerCase().includes('baja')
            );

            const updates = consentimientosPoseedor.map((row: any) =>
              this.consentimientoPoseedorControllerSrv.updateById(row.id, {
                ...row,
                estado: estadoBaja ? { id: estadoBaja.id } : row?.estado,
                fechaBaja: new Date().toISOString(),
              } as any)
            );

            return forkJoin(updates);
          })
        );
      })
    ).subscribe({
      next: (result) => {
        if (!result) {
          return;
        }
        this.spinnerSrv.loadingHide();
        this.selectedPoseedorIds = [];
        this.poseedoresConsentimiento = this.withCheckedFlag(this.poseedoresConsentimiento, []);
        this.loadPoseedoresMismoDocumentoNoIncluidos();
        this.snackBarSrv.showSnackBar('NIMAs dados de baja correctamente', 'success');
      },
      error: () => {
        this.spinnerSrv.loadingHide();
        this.snackBarSrv.showSnackBar('Ha ocurrido un error al dar de baja los NIMAs seleccionados', 'error');
      },
    });
  }

  onIncludedChecked(item: any): void {
    const poseedorId = item?.id;
    if (!poseedorId) {
      return;
    }
    if (item?.checked) {
      if (!this.selectedPoseedorIds.includes(poseedorId)) {
        this.selectedPoseedorIds = [...this.selectedPoseedorIds, poseedorId];
      }
      return;
    }
    this.selectedPoseedorIds = this.selectedPoseedorIds.filter((id) => id !== poseedorId);
  }

  onAvailableChecked(item: any): void {
    const poseedorId = item?.id;
    if (!poseedorId) {
      return;
    }
    if (item?.checked) {
      if (!this.selectedAvailablePoseedorIds.includes(poseedorId)) {
        this.selectedAvailablePoseedorIds = [...this.selectedAvailablePoseedorIds, poseedorId];
      }
      return;
    }
    this.selectedAvailablePoseedorIds = this.selectedAvailablePoseedorIds.filter((id) => id !== poseedorId);
  }

  private loadPoseedoresMismoDocumentoNoIncluidos(): void {
    const codigoDocumento = this.getCodigoDocumentoConsentimiento();
    if (!codigoDocumento) {
      this.poseedoresMismoDocumentoNoIncluidos = [];
      return;
    }

    this.poseedorControllerService.findAll5({ codigoDocumento: [codigoDocumento] }, { page: 0, size: 500, sort: ['id,desc'] }).subscribe({
      next: (res: any) => {
        const idsActuales = new Set((this.poseedoresConsentimiento ?? []).map((item: any) => item?.id));
        const poseedores = res?.datos ?? [];
        this.poseedoresMismoDocumentoNoIncluidos = this.withCheckedFlag(
          poseedores.filter((item: any) => item?.id && !idsActuales.has(item.id)),
          this.selectedAvailablePoseedorIds
        );
      },
      error: () => {
        this.poseedoresMismoDocumentoNoIncluidos = [];
      },
    });
  }

  private getCodigoDocumentoConsentimiento(): string {
    const codigo = this.consentimiento?.codigoDocumento ?? this.consentimiento?.poseedor?.codigoDocumento ?? '';
    return codigo.toString().trim().toUpperCase();
  }

  private withCheckedFlag(list: any[], selectedIds: number[]): any[] {
    const selectedSet = new Set(selectedIds);
    return (list ?? []).map((item: any) => ({
      ...item,
      checked: selectedSet.has(item?.id),
    }));
  }
}
